import React, { FC } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';

import { CategoryFilter } from 'Jsx/Catalog/types';

import BorderContainer from '../BorderContainer';
import { useAppContext } from '../../context/AppContext';
import HamburgerMenu from '../HamburgerMenu';

import Filter from './components/SharedFilter';
import LanguageFilter from './components/LanguageFilter';
import { getSoftwaresSelectedIds, getLevelsSelectedIds } from './utils';
import FilterSubItemContainer from './components/FilterSubItemContainer';
import DurationFilter from './components/DurationFilter';

interface FiltersProps {
  scrollTop?: () => void;
}

const Filters: FC<FiltersProps> = ({ scrollTop }) => {
  const {
    filters,
    filters: { categories, levels, softwares, categoryOpen, categorySelected },
    filterActions,
    orderByActions,
    setCurrentPage,
  } = useAppContext();
  const softwaresSelectedIds = getSoftwaresSelectedIds(filters);
  const levelsSelectedIds = getLevelsSelectedIds(filters);
  const { logEvent } = useAmplitude();
  const { t, countryCode } = useTranslation();

  const filterBySubcategory = (itemId: number, category: CategoryFilter) => {
    filterActions.toggleFilterSubcategory(itemId);
    setCurrentPage(1);

    const subcategory = category.subItems[itemId];

    logEvent({
      event: 'Catalog Filter',
      data: {
        FilterType: 'Subcategory',
        SubcategoryId: itemId,
        CategoryId: category.id,
        CourseCategory: category.name,
        CourseSubCategory: subcategory?.name,
        ...getSourceEventParams(),
      },
    });

    scrollTop?.();
  };

  const filterByCategory = (category: CategoryFilter) => {
    filterActions.toggleFilterCategory({
      itemId: category.id,
      slug: category.slug,
      subItemId: 0,
    });
    setCurrentPage(1);

    logEvent({
      event: 'Catalog Filter',
      data: {
        FilterType: 'Category',
        CategoryId: category.id,
        CourseCategory: category.name,
        ...getSourceEventParams(),
      },
    });

    const hasCategorySelected = !!categoryOpen.itemId;

    if (!hasCategorySelected) {
      orderByActions.setDefaultOrderBy();
    }

    scrollTop?.();
  };

  const renderCategoryFilters = () => {
    return Object.values(categories).map(category => {
      const open = categoryOpen.itemId === category.id;

      return (
        <li key={category.id} aria-label={category?.name}>
          <Filter
            item={category}
            toggleSubItem={itemId => filterBySubcategory(itemId, category)}
            openFilter={() => filterByCategory(category)}
            subItemsSelected={
              categorySelected.subItemId !== null
                ? [categorySelected.subItemId]
                : []
            }
            open={open}
            className="my-16"
            hideMaxHeight
            enableHiddenOverflow
            automaticScrollToSelected
          />
        </li>
      );
    });
  };

  return (
    <>
      <h3 className="text-12 font-semibold text-gray-dark uppercase mt-20 mb-0">
        {t('CATALOG_FILTER_RESULTS')}
      </h3>

      <BorderContainer hideBorderTop>
        <HamburgerMenu label={t('CATALOG_CATEGORIES')} isDefaultOpen>
          <FilterSubItemContainer>
            <ul>{renderCategoryFilters()}</ul>
          </FilterSubItemContainer>
        </HamburgerMenu>
      </BorderContainer>

      {/* Levels */}
      <BorderContainer hideBorderTop>
        <Filter
          multipleSelection
          item={levels}
          isMainTitle
          toggleSubItem={itemId => {
            filterActions.toggleFilterLevel(itemId);

            logEvent({
              event: 'Catalog Filter',
              data: {
                FilterType: 'Level',
                LevelId: itemId,
                ...getSourceEventParams(),
              },
            });

            scrollTop?.();
          }}
          openFilter={filterActions.toggleLevelsOpen}
          subItemsSelected={levelsSelectedIds}
          open={levels.open}
          reorderDisabledItems={false}
        />
      </BorderContainer>

      {/* Softwares */}
      <BorderContainer hideBorderTop>
        <Filter
          multipleSelection
          item={softwares}
          isMainTitle
          toggleSubItem={itemId => {
            filterActions.toggleFilterSoftware(itemId);

            logEvent({
              event: 'Catalog Filter',
              data: {
                FilterType: 'Software',
                SoftwareId: itemId,
                ...getSourceEventParams(),
              },
            });

            scrollTop?.();
          }}
          openFilter={filterActions.toggleSoftwareOpen}
          subItemsSelected={softwaresSelectedIds}
          open={softwares.open}
        />
      </BorderContainer>

      {/* Duration */}
      <BorderContainer hideBorderTop>
        <DurationFilter scrollTop={scrollTop} />
      </BorderContainer>

      {/* Language */}
      {countryCode === 'br' ? null : (
        <BorderContainer hideBorders>
          <LanguageFilter scrollTop={scrollTop} />
        </BorderContainer>
      )}
    </>
  );
};

export default Filters;
